import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  AfterViewInit
} from '@angular/core';

/**
 * Directive that shows tooltip only when text content overflows
 * Automatically sets the title attribute only when ellipsis is active
 *
 * Performance optimized: checks overflow only on initialization and mouse interaction
 * to avoid creating thousands of ResizeObservers in large tables
 *
 * Usage:
 * <div appShowTooltipOnOverflow>Long text that might overflow...</div>
 */
@Directive({
  selector: '[appShowTooltipOnOverflow]',
  standalone: true
})
export class ShowTooltipOnOverflowDirective implements AfterViewInit {
  private readonly originalContent: string;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {
    // Store original text content
    this.originalContent = this.el.nativeElement.textContent?.trim() ?? '';
  }

  ngAfterViewInit(): void {
    // Initial check only
    this.checkOverflow();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    // Re-check on mouse enter for dynamic content changes
    this.checkOverflow();
  }

  /**
   * Check if element content is overflowing
   * Sets or removes title attribute accordingly
   */
  private checkOverflow(): void {
    const element = this.el.nativeElement;

    // Get tooltip content from data-tooltip attribute
    const dataTooltip = element.getAttribute('data-tooltip');

    // Check if there's a data-tooltip-always attribute (for dates and special cases)
    const alwaysShowTooltip = element.hasAttribute('data-tooltip-always');

    // Check if scrollWidth exceeds clientWidth (horizontal overflow)
    const isOverflowing = element.scrollWidth > element.clientWidth;

    // Show tooltip if overflowing OR if always-show flag is set
    if (isOverflowing || alwaysShowTooltip) {
      const tooltipContent = dataTooltip ?? this.originalContent;

      // Only set if not already set to avoid unnecessary DOM updates
      if (element.title !== tooltipContent) {
        this.renderer.setAttribute(element, 'title', tooltipContent);
      }
    } else {
      // Remove title when not overflowing and not always-show
      if (element.hasAttribute('title')) {
        this.renderer.removeAttribute(element, 'title');
      }
    }
  }
}
